/** @format */

import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, useMediaQuery } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
import { QueryProvider } from "./services/python-api/QueryProvider";
import { TourProvider, Tour } from "./components/Tour";
import { jwtDecode } from "jwt-decode";
import { chatService } from "./services/chatService";
import { authService } from "./services/authService";
import { useActivityTracking } from "./utils/useActivityTracking";
import theme from "./theme";
import "./index.css";
import Layout from "./components/Layout";
import AppShellLoader from "./components/AppShellLoader";
import KeyboardAccessibilityHelper from "./components/KeyboardAccessibilityHelper";
import "leaflet/dist/leaflet.css";
import useUserStore from "./store/userStore";
import { getToken } from "./utils/tokenStorage";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthChecker from "./components/AuthChecker";
import { runAguiStreamTurn } from "./features/chat-agui/runAguiStreamTurn";
import { createAguiIds } from "./features/chat-agui/aguiStream";
import { useVoiceSession } from "./features/chat-agui/hooks/useVoiceSession";
import { useVoiceNoteRecorder } from "./features/chat-agui/hooks/useVoiceNoteRecorder";
import { openPaymentFromStreamEvent } from "./features/chat-agui/openPaymentFromStream";
import { normalizeAguiComponents } from "./features/chat-agui/normalizeComponents";
import { isVoiceSupported } from "./features/chat-agui/voiceSession";
import toast from "react-hot-toast";
import { parseAgentResponseWithUi } from "./features/chat-agui/parseBatchResponse";
import { mapAndEnrichChatMessages } from "./features/chat-agui/mapChatMessages";
import { mergeSavedVoiceMessagesIntoList } from "./features/chat-agui/mergeSavedVoiceMessages";
import { transformApiChatToListItem, unwrapChatMessagesResponse, mergeChatListUpdates } from "./utils/chatListMappers";
import { CHATS_PAGE_SIZE, parseChatsPageResponse } from "./features/chats/chatListPagination";
import { resolveAgentUiForDisplay, sanitizeEnrichedChatMessages } from "./features/chat-agui/aguiDisplayUtils";
import { appendAguiTurnCache } from "./utils/aguiUiCache";
import { buildTrackingPagePath, isTrackingMapUiAction } from "./utils/trackingMapNavigation";
import {
  enrichUiWithTrackingMap,
  isTrackLocateIntent,
  createEphemeralTrackingThreadId,
  fetchTrackingMapUiBlocks,
  resolveTrackingCodeForMap,
  filterChatTrackingUi,
} from "./features/chat-agui/enrichTrackingMapUi";

import ChatsRoute from "./routes/ChatsRoute";
import ShipmentsRoute from "./routes/ShipmentsRoute";
import DocumentsRoute from "./routes/DocumentsRoute";
import CustomerRoute from "./routes/CustomerRoute";
import WorkWithDockie from "./pages/vendors/WorkWithDockie";
import VendorHome from "./pages/vendors/VendorHome";

const SignUp = lazy(() => import("./pages/auth/sign-up"));
const LoginPage = lazy(() => import("./pages/auth/sign-in"));
const CompleteProfile = lazy(() => import("./pages/auth/complete-profile"));
const ForgotPassword = lazy(() => import("./pages/auth/forgot-password"));
const OTPVerification = lazy(() => import("./pages/auth/otp-verification"));
const ResetPassword = lazy(() => import("./pages/auth/reset-password"));
const Dashboard = lazy(() => import("./pages/dashboard"));
const Version4 = lazy(() => import("./pages/LandingPrototype/Version4"));
const Transporter = lazy(() => import("./pages/trasporterpage"));
const Blog = lazy(() => import("./pages/blog/Blog"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const AnalyticsPage = lazy(() => import("./pages/analytics"));
const Settings = lazy(() => import("./pages/settings"));
const TrackingPage = lazy(() => import("./pages/tracking"));

const PageLoader = ({ showSidebar = true }) => <AppShellLoader showSidebar={showSidebar} />;
// Storage keys for persistence
const STORAGE_KEYS = {
  CHATS: "dockie_chat_history",
  CURRENT_CHAT: "dockie_current_chat_id",
  ARTIFACTS: "dockie_artifacts",
  USER_PREFERENCES: "dockie_user_preferences",
};

export default function App() {
  const { initializeAuth, user } = useUserStore();

  // Activity tracking
  const token = getToken();
  useActivityTracking(token);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(false);
  const [chatsOffset, setChatsOffset] = useState(0);
  const [chatsLoadingMore, setChatsLoadingMore] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesLoadError, setMessagesLoadError] = useState(null);
  const [newChatLoading, setNewChatLoading] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [userPreferences, setUserPreferences] = useState({
    name: "",
    askedForName: false,
    registrationNudgeCount: 0,
  });
  const aguiStreamAbortRef = useRef(null);
  const voiceLiveMsgIdRef = useRef(null);
  const voiceUserMsgIdRef = useRef(null);
  const voiceSessionModeRef = useRef(null);
  const [voiceNoteRecorded, setVoiceNoteRecorded] = useState(false);
  const [voiceNoteDraft, setVoiceNoteDraft] = useState("");
  const [voiceNoteAttachment, setVoiceNoteAttachment] = useState(null);
  /** In-memory cache so revisiting a chat does not refetch from scratch */
  const messagesCacheRef = useRef({});
  const hydrateInFlightRef = useRef(null);
  const chatsLoadMoreInFlightRef = useRef(false);

  // Mobile responsiveness
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // React Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  const showSidebarLoader = !location.pathname.startsWith("/auth") && location.pathname !== "/";
  useEffect(() => {
    if (selectedChatId && messages.length > 0) {
      messagesCacheRef.current[selectedChatId] = sanitizeEnrichedChatMessages(messages);
    }
  }, [messages, selectedChatId]);

  const getPreviousAgentUi = useCallback((list = []) => {
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (list[i]?.role === "agent" && Array.isArray(list[i].uiComponents)) {
        return list[i].uiComponents;
      }
    }
    return [];
  }, []);

  const canAccessChatApi = useCallback(() => {
    const token = getToken();
    const { isAuthenticated, _hasHydrated } = useUserStore.getState();
    return Boolean(token && isAuthenticated && _hasHydrated);
  }, []);

  const applyCachedMessages = useCallback((chatId) => {
    if (!Object.prototype.hasOwnProperty.call(messagesCacheRef.current, chatId)) {
      return false;
    }
    const cached = messagesCacheRef.current[chatId] ?? [];
    setMessages(cached);
    setMessagesLoading(false);
    setMessagesLoadError(null);
    return true;
  }, []);

  const hydrateSelectedChatMessages = useCallback(
    async (chatId) => {
      if (!chatId) return [];
      if (!canAccessChatApi()) {
        setMessagesLoading(false);
        return [];
      }
      if (hydrateInFlightRef.current === chatId) {
        return messagesCacheRef.current[chatId] ?? [];
      }

      const alreadyCached = Object.prototype.hasOwnProperty.call(messagesCacheRef.current, chatId);
      if (alreadyCached) {
        const cached = messagesCacheRef.current[chatId] ?? [];
        setMessages(cached);
        setMessagesLoading(false);
        setMessagesLoadError(null);
        return cached;
      }

      hydrateInFlightRef.current = chatId;
      setMessagesLoadError(null);
      setMessages([]);
      setMessagesLoading(true);

      try {
        const response = await chatService.getChatMessages(chatId);
        const rows = unwrapChatMessagesResponse(response);
        const { ipTimezone, ipOffsetMinutes } = useUserStore.getState();
        const apiMessages = await mapAndEnrichChatMessages(rows, chatId, {
          userId: user?.id || user?._id,
          timeZone: ipTimezone,
          offsetMinutes: ipOffsetMinutes,
        });
        if (hydrateInFlightRef.current === chatId) {
          setMessages(apiMessages);
          messagesCacheRef.current[chatId] = apiMessages;
        }
        return apiMessages;
      } catch (error) {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail ?? "";
        const notFound = status === 404 || /not found/i.test(String(detail));
        if (hydrateInFlightRef.current === chatId) {
          setMessages([]);
          messagesCacheRef.current[chatId] = [];
          setMessagesLoadError(notFound ? null : "Couldn't load this conversation. Please try again.");
        }
        return [];
      } finally {
        if (hydrateInFlightRef.current === chatId) {
          hydrateInFlightRef.current = null;
          setMessagesLoading(false);
        }
      }
    },
    [user?.id, user?._id, canAccessChatApi],
  );

  const resetVoiceTurnDraftRefs = useCallback(() => {
    voiceLiveMsgIdRef.current = null;
    voiceUserMsgIdRef.current = null;
  }, []);

  const mergeSavedVoiceTurn = useCallback(
    (savedMessages) => {
      if (!Array.isArray(savedMessages) || !savedMessages.length) return;
      setMessages((prev) => {
        const next = mergeSavedVoiceMessagesIntoList(prev, savedMessages, selectedChatId);
        if (selectedChatId) {
          messagesCacheRef.current[selectedChatId] = sanitizeEnrichedChatMessages(next);
        }
        return next;
      });
      resetVoiceTurnDraftRefs();
    },
    [selectedChatId, resetVoiceTurnDraftRefs],
  );

  const finalizeStreamingPlaceholders = useCallback(() => {
    setMessages((prev) =>
      prev
        .map((message) =>
          message.isStreaming
            ? {
                ...message,
                isStreaming: false,
                streamStatus: null,
                streamTool: null,
                streamTools: [],
              }
            : message,
        )
        .filter(
          (message) =>
            !(
              message.role === "agent" &&
              !String(message.text ?? "").trim() &&
              !(Array.isArray(message.uiComponents) && message.uiComponents.length)
            ),
        ),
    );
  }, []);

  const finalizeVoiceTurnInMessages = useCallback((userText, assistantText) => {
    setMessages((prev) => {
      let next = [...prev];
      const liveId = voiceLiveMsgIdRef.current;
      const userId = voiceUserMsgIdRef.current;

      if (userText) {
        if (userId && next.some((m) => m.id === userId)) {
          next = next.map((m) => (m.id === userId ? { ...m, text: userText } : m));
        } else if (!next.some((m) => m.role === "user" && m.text === userText)) {
          const id = userId ?? `voice_user_${Date.now()}`;
          voiceUserMsgIdRef.current = id;
          next.push({
            id,
            role: "user",
            text: userText,
            timestamp: new Date(),
          });
        }
      }

      if (liveId) {
        if (next.some((m) => m.id === liveId)) {
          next = next.map((m) =>
            m.id === liveId
              ? {
                  ...m,
                  text: assistantText || m.text,
                  isStreaming: false,
                  streamStatus: null,
                }
              : m,
          );
        } else if (assistantText) {
          next.push({
            id: liveId,
            role: "agent",
            text: assistantText,
            timestamp: new Date(),
          });
        }
      } else if (
        assistantText &&
        !next.some((m) => m.role === "agent" && m.text === assistantText)
      ) {
        next.push({
          id: `voice_agent_${Date.now()}`,
          role: "agent",
          text: assistantText,
          timestamp: new Date(),
        });
      }

      return next;
    });
  }, []);

  const voiceSession = useVoiceSession({
    threadId: selectedChatId,
    chatId: selectedChatId,
    onError: (message) => {
      toast.error(message);
      voiceSessionModeRef.current = null;
    },
    onTurnComplete: (saved, transcripts) => {
      if (voiceSessionModeRef.current === "chat") {
        finalizeVoiceTurnInMessages(
          transcripts?.userTranscript?.trim(),
          transcripts?.assistantTranscript?.trim(),
        );
      }
      resetVoiceTurnDraftRefs();
      // Refetching here would replace the transcript with the server copy the
      // backend just told us it does not have, so the turn stays local instead.
      if (saved === false && voiceSessionModeRef.current === "chat") {
        toast.error("This voice turn could not be saved to the chat history.");
      }
    },
    onComponents: (components) => {
      if (voiceSessionModeRef.current !== "chat") return;
      const normalized = normalizeAguiComponents(components);
      if (!normalized.length) return;
      const liveId = voiceLiveMsgIdRef.current ?? `voice_live_${Date.now()}`;
      voiceLiveMsgIdRef.current = liveId;
      setMessages((prev) => {
        const existing = prev.find((m) => m.id === liveId);
        if (existing) {
          const prior = Array.isArray(existing.uiComponents) ? existing.uiComponents : [];
          const seen = new Set(prior.map((component) => component?.id).filter(Boolean));
          const additions = normalized.filter(
            (component) => !component?.id || !seen.has(component.id),
          );
          return prev.map((m) =>
            m.id === liveId
              ? { ...m, uiComponents: [...prior, ...additions], isAgui: true }
              : m,
          );
        }
        return [
          ...prev,
          {
            id: liveId,
            role: "agent",
            text: "",
            uiComponents: normalized,
            isAgui: true,
            isStreaming: true,
            timestamp: new Date(),
          },
        ];
      });
    },
    onPaymentEvent: (event) => openPaymentFromStreamEvent(event, user),
    onTurnSaved: (savedMessages) => {
      mergeSavedVoiceTurn(savedMessages);
    },
  });

  const voiceNote = useVoiceNoteRecorder({
    chatId: selectedChatId,
    onError: (message, error) => {
      setVoiceNoteRecorded(false);
      setVoiceNoteDraft("");
      setVoiceNoteAttachment(null);
      voiceSessionModeRef.current = null;
      if (error?.status === 422) toast.error("Didn't catch that — try recording again.");
      else if (error?.status === 502) {
        toast.error("Voice transcription is unavailable. Try again or type your message.");
      } else {
        toast.error(message);
      }
    },
    onReady: ({ transcript, voiceNote: attachment }) => {
      // Never auto-send: the caller must review/edit the transcript first.
      setVoiceNoteDraft(transcript);
      setVoiceNoteAttachment(attachment);
      setVoiceNoteRecorded(true);
      voiceSessionModeRef.current = null;
    },
  });
  const {
    cancel: cancelVoiceNote,
    start: startVoiceNote,
    stop: stopVoiceNote,
  } = voiceNote;
  const { resetTranscripts: resetVoiceTranscripts } = voiceSession;

  useEffect(() => {
    setVoiceNoteRecorded(false);
    setVoiceNoteDraft("");
    setVoiceNoteAttachment(null);
    cancelVoiceNote();
    resetVoiceTranscripts();
    voiceLiveMsgIdRef.current = null;
    voiceUserMsgIdRef.current = null;
    voiceSessionModeRef.current = null;
  }, [selectedChatId, cancelVoiceNote, resetVoiceTranscripts]);

  useEffect(() => {
    if (voiceSession.mode !== "chat" || !voiceSession.userTranscript.trim()) return;
    const userText = voiceSession.userTranscript.trim();
    const userId = voiceUserMsgIdRef.current ?? `voice_user_${Date.now()}`;
    voiceUserMsgIdRef.current = userId;
    setMessages((prev) => {
      if (prev.some((m) => m.id === userId)) {
        return prev.map((m) => (m.id === userId ? { ...m, text: userText } : m));
      }
      return [
        ...prev,
        {
          id: userId,
          role: "user",
          text: userText,
          timestamp: new Date(),
        },
      ];
    });
  }, [voiceSession.mode, voiceSession.userTranscript]);

  useEffect(() => {
    if (voiceSession.mode !== "chat" || !voiceSession.assistantTranscript.trim()) return;
    const liveId = voiceLiveMsgIdRef.current ?? `voice_live_${Date.now()}`;
    voiceLiveMsgIdRef.current = liveId;
    setMessages((prev) => {
      if (prev.some((m) => m.id === liveId)) {
        return prev.map((m) =>
          m.id === liveId
            ? {
                ...m,
                text: voiceSession.assistantTranscript,
                isStreaming: true,
                streamStatus: null,
              }
            : m,
        );
      }
      return [
        ...prev,
        {
          id: liveId,
          role: "agent",
          text: voiceSession.assistantTranscript,
          uiComponents: [],
          isStreaming: true,
          timestamp: new Date(),
        },
      ];
    });
  }, [voiceSession.mode, voiceSession.assistantTranscript]);

  const ensureChatForVoice = useCallback(() => {
    if (selectedChatId && !selectedChatId.startsWith("temp_")) return selectedChatId;
    toast.error("Please start or select a chat first.");
    return null;
  }, [selectedChatId]);

  const handleStartVoiceChat = useCallback(async () => {
    if (!ensureChatForVoice()) return;
    if (loading) {
      toast.error("Wait for the current response to finish before starting live voice.");
      return;
    }
    if (!isVoiceSupported()) {
      toast.error("Voice is not supported in this browser.");
      return;
    }
    setVoiceNoteRecorded(false);
    setVoiceNoteDraft("");
    voiceSessionModeRef.current = "chat";
    await voiceSession.start("chat");
  }, [ensureChatForVoice, loading, voiceSession]);

  const handleStartVoiceNote = useCallback(async () => {
    if (!ensureChatForVoice()) return;
    if (loading || voiceSession.isActive) {
      toast.error("Wait for the current chat turn to finish before recording a voice note.");
      return;
    }
    setVoiceNoteRecorded(false);
    setVoiceNoteDraft("");
    setVoiceNoteAttachment(null);
    voiceSessionModeRef.current = "note";
    const started = await startVoiceNote();
    if (!started) voiceSessionModeRef.current = null;
  }, [ensureChatForVoice, loading, voiceSession.isActive, startVoiceNote]);

  const handleStopVoiceChat = useCallback(async () => {
    const userText = voiceSession.userTranscript.trim();
    const assistantText = voiceSession.assistantTranscript.trim();
    finalizeVoiceTurnInMessages(userText, assistantText);
    await voiceSession.stop();
    voiceSessionModeRef.current = null;
  }, [voiceSession, finalizeVoiceTurnInMessages]);

  const handleCancelVoice = useCallback(async () => {
    setVoiceNoteRecorded(false);
    setVoiceNoteDraft("");
    setVoiceNoteAttachment(null);
    if (voiceSessionModeRef.current === "note" || voiceNote.isActive) {
      cancelVoiceNote();
      voiceSessionModeRef.current = null;
      return;
    }
    voiceSessionModeRef.current = null;
    voiceSession.clearSessionError();
    await voiceSession.cancel();
  }, [cancelVoiceNote, voiceNote.isActive, voiceSession]);

  const handleClearVoiceSessionError = useCallback(() => {
    voiceSession.clearSessionError();
    voiceSessionModeRef.current = null;
    setVoiceNoteRecorded(false);
    setVoiceNoteDraft("");
    setVoiceNoteAttachment(null);
    cancelVoiceNote();
  }, [cancelVoiceNote, voiceSession]);

  const handleConfirmVoiceNote = useCallback(async () => {
    await stopVoiceNote();
  }, [stopVoiceNote]);

  const handleVoiceNoteEdited = useCallback(() => {
    setVoiceNoteAttachment(null);
  }, []);

  const handleVoiceNoteDraftConsumed = useCallback(() => {
    setVoiceNoteDraft("");
    setVoiceNoteRecorded(false);
    setVoiceNoteAttachment(null);
    resetVoiceTranscripts();
  }, [resetVoiceTranscripts]);

  // Listen for payment confirmation events to refresh messages
  useEffect(() => {
    const handleRefreshMessages = async () => {
      if (!canAccessChatApi()) return;
      if (selectedChatId) {
        try {
          const response = await chatService.getChatMessages(selectedChatId);
          const rows = unwrapChatMessagesResponse(response);
          if (rows.length) {
            const { ipTimezone, ipOffsetMinutes } = useUserStore.getState();
            const apiMessages = await mapAndEnrichChatMessages(rows, selectedChatId, {
              userId: user?.id || user?._id,
              timeZone: ipTimezone,
              offsetMinutes: ipOffsetMinutes,
            });
            setMessages(apiMessages);
            messagesCacheRef.current[selectedChatId] = apiMessages;
          }
        } catch (error) {
          console.error("Failed to refresh messages:", error);
        }
      }
    };

    const handleRefreshChatList = async () => {
      if (!canAccessChatApi()) return;
      const userId = user?._id || user?.id;
      if (userId && userId !== "anonymous") {
        try {
          const res = await chatService.getUserChats(userId, CHATS_PAGE_SIZE, 0);
          const { chats } = parseChatsPageResponse(res);
          if (chats?.length) {
            const transformedChats = chats.map((chat) => transformApiChatToListItem(chat, { selectedChatId }));
            setChatHistory((prev) => mergeChatListUpdates(prev, transformedChats));
          }
        } catch (error) {
          console.error("Failed to refresh chat list:", error);
        }
      }
    };

    window.addEventListener("refreshMessages", handleRefreshMessages);
    window.addEventListener("refreshChatList", handleRefreshChatList);
    return () => {
      window.removeEventListener("refreshMessages", handleRefreshMessages);
      window.removeEventListener("refreshChatList", handleRefreshChatList);
    };
  }, [selectedChatId, user?._id, user?.id, canAccessChatApi]);

  // Handle URL-based chat loading
  useEffect(() => {
    const path = location.pathname;
    const chatMatch = path.match(/\/chats\/([^/]+)/);

    if (chatMatch && !canAccessChatApi()) {
      return;
    }

    if (!chatMatch) return;

    const chatId = chatMatch[1];
    if (chatId === "new") {
      setSelectedChatId(null);
      setMessages([]);
      setMessagesLoading(false);
      return;
    }
    if (chatId.startsWith("temp_")) {
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const userData = {
          _id: decoded.id,
          email: decoded.email,
          display_name: decoded.display_name,
          user_type: decoded.user_type,
          isAdmin: decoded.admin === "admin",
          permissions: decoded.permissions,
        };

        const { login } = useUserStore.getState();
        login(userData, token);

        const cleanUrl =
          window.location.pathname + window.location.search.replace(/[?&]token=[^&]*/, "").replace(/^&/, "?");
        window.history.replaceState({}, "", cleanUrl);
      } catch (error) {
        console.error("Failed to authenticate with URL token:", error);
      }
    }

    if (chatId === selectedChatId) {
      return;
    }

    if (!applyCachedMessages(chatId)) {
      setMessages([]);
      setMessagesLoading(true);
    }
    setSelectedChatId(chatId);
    setCurrentSessionId(chatId);
    hydrateSelectedChatMessages(chatId);
  }, [
    location.pathname,
    location.search,
    selectedChatId,
    hydrateSelectedChatMessages,
    applyCachedMessages,
    canAccessChatApi,
  ]);

  // Update mobile state when screen size changes
  useEffect(() => {
    // No sidebar state management here - let Layout handle it
  }, [isMobile]);

  // Initialize auth
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Fetch IP-based timezone on mount — always, regardless of auth state
  useEffect(() => {
    const fetchIpTimezone = async () => {
      try {
        const userInfo = await authService.getUserInfo();
        if (userInfo?.data) {
          authService.postUserInfo(userInfo.data).catch(() => {});
          const ipTz = userInfo.data.timezone || userInfo.data.time_zone;
          if (ipTz) {
            const ipOffset =
              userInfo.data.offset_minutes ??
              userInfo.data.utcOffset ??
              (() => {
                try {
                  const offsetStr =
                    new Intl.DateTimeFormat("en", {
                      timeZone: ipTz,
                      timeZoneName: "shortOffset",
                    })
                      .formatToParts(new Date())
                      .find((p) => p.type === "timeZoneName")?.value ?? "UTC+0";
                  const match = offsetStr.match(/UTC([+-])(\d+)(?::(\d+))?/);
                  if (!match) return 0;
                  const sign = match[1] === "+" ? 1 : -1;
                  return sign * (parseInt(match[2]) * 60 + parseInt(match[3] ?? "0"));
                } catch {
                  return 0;
                }
              })();
            useUserStore.getState().setIpTimezone(ipTz, ipOffset);
            localStorage.setItem("ip_timezone", ipTz);
            localStorage.setItem("ip_offset_minutes", String(ipOffset));
          }
        }
      } catch (error) {
        console.warn("IP timezone fetch failed (non-critical):", error);
      }
    };
    fetchIpTimezone();
  }, []);

  // Track current user to detect user changes
  const [currentUserId, setCurrentUserId] = useState(null);

  // Clear chat data immediately when user changes (before loading new chats)
  useEffect(() => {
    const userId = user?._id || user?.id;

    if (currentUserId !== null && currentUserId !== userId) {
      setChatHistory([]);
      setMessages([]);
      setSelectedChatId(null);
      setCurrentSessionId(null);
      setArtifacts([]);
      setMessagesLoading(false);
      setChatsOffset(0);
      setHasMoreChats(false);
      chatsLoadMoreInFlightRef.current = false;
      hydrateInFlightRef.current = null;
      messagesCacheRef.current = {};
    }

    setCurrentUserId(userId);
  }, [user?._id, user?.id, currentUserId]);

  const mapChatsPage = useCallback(
    (chats, activeId = null) => chats.map((chat) => transformApiChatToListItem(chat, { selectedChatId: activeId })),
    [],
  );

  const loadMoreChats = useCallback(async () => {
    const userId = user?._id || user?.id;
    if (!userId || userId === "anonymous" || chatsLoadingMore || chatsLoadMoreInFlightRef.current || !hasMoreChats) {
      return;
    }

    chatsLoadMoreInFlightRef.current = true;
    setChatsLoadingMore(true);
    try {
      const res = await chatService.getUserChats(userId, CHATS_PAGE_SIZE, chatsOffset);
      const { chats, hasMore } = parseChatsPageResponse(res);

      // Empty page → nothing more to load; stop to avoid an infinite trigger loop.
      if (!chats.length) {
        setHasMoreChats(false);
        return;
      }

      const transformed = mapChatsPage(chats);
      let appendedCount = 0;
      setChatHistory((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const fresh = transformed.filter((c) => !seen.has(c.id));
        appendedCount = fresh.length;
        return [...prev, ...fresh];
      });
      setChatsOffset((o) => o + chats.length);
      // If the whole page was already present (all duplicates), don't keep asking.
      setHasMoreChats(hasMore && (appendedCount > 0 || chats.length === CHATS_PAGE_SIZE));
    } catch (error) {
      console.error("Failed to load more chats:", error);
    } finally {
      chatsLoadMoreInFlightRef.current = false;
      setChatsLoadingMore(false);
    }
  }, [user?._id, user?.id, chatsOffset, hasMoreChats, chatsLoadingMore, mapChatsPage]);

  // Load data from localStorage and API on mount
  useEffect(() => {
    const loadChats = async () => {
      const userId = user?._id || user?.id;
      const hasToken = getToken();

      if (!user?.isAuthenticated && !hasToken) {
        setChatsLoading(false);
        return;
      }

      setChatsLoading(true);
      try {
        if (userId && userId !== "anonymous") {
          const res = await chatService.getUserChats(userId, CHATS_PAGE_SIZE, 0);
          const { chats, hasMore } = parseChatsPageResponse(res);

          if (chats.length) {
            setChatHistory(mapChatsPage(chats, null));
          }
          setChatsOffset(chats.length);
          setHasMoreChats(hasMore);
        }
      } catch (error) {
        console.error("Failed to load chats from API:", error);
      }

      setChatsLoading(false);
    };

    const savedCurrentChatId = localStorage.getItem(STORAGE_KEYS.CURRENT_CHAT);
    const savedArtifacts = localStorage.getItem(STORAGE_KEYS.ARTIFACTS);
    const savedUserPreferences = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);

    // Load user preferences
    if (savedUserPreferences) {
      try {
        const preferences = JSON.parse(savedUserPreferences);
        setUserPreferences(preferences);
      } catch (error) {
        console.error("Failed to load user preferences:", error);
        localStorage.removeItem(STORAGE_KEYS.USER_PREFERENCES);
      }
    }

    // Load artifacts
    if (savedArtifacts) {
      try {
        const parsedArtifacts = JSON.parse(savedArtifacts);
        setArtifacts(parsedArtifacts);
      } catch (error) {
        console.error("Failed to load artifacts:", error);
        localStorage.removeItem(STORAGE_KEYS.ARTIFACTS);
      }
    }
    // Run the loader
    loadChats();

    // Restore last selected chat only when authenticated (avoid 401 loops on /auth/login)
    if (savedCurrentChatId && getToken() && user?.isAuthenticated) {
      setTimeout(() => {
        if (!canAccessChatApi()) return;
        if (!applyCachedMessages(savedCurrentChatId)) {
          setMessages([]);
          setMessagesLoading(true);
        }
        setSelectedChatId(savedCurrentChatId);
        setCurrentSessionId(savedCurrentChatId);
        setChatHistory((prev) =>
          prev.map((chat) => ({
            ...chat,
            isActive: chat.id === savedCurrentChatId,
          })),
        );
        hydrateSelectedChatMessages(savedCurrentChatId);
      }, 100);
    }
  }, [
    user?.isAuthenticated,
    user?._id,
    user?.id,
    hydrateSelectedChatMessages,
    applyCachedMessages,
    canAccessChatApi,
    mapChatsPage,
  ]);

  // Save data to localStorage whenever it changes and sort by lastMessage
  useEffect(() => {
    if (chatHistory.length > 0) {
      const sortedChats = [...chatHistory].sort(
        (a, b) => new Date(b.lastMessage || b.timestamp) - new Date(a.lastMessage || a.timestamp),
      );
      localStorage.setItem(STORAGE_KEYS.CHATS, JSON.stringify(sortedChats));
    }
  }, [chatHistory]);

  useEffect(() => {
    if (selectedChatId) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_CHAT, selectedChatId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_CHAT);
    }
  }, [selectedChatId]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ARTIFACTS, JSON.stringify(artifacts));
  }, [artifacts]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(userPreferences));
  }, [userPreferences]);

  const updateChatInHistory = (chatId, updates) => {
    setChatHistory((prev) => prev.map((chat) => (chat.id === chatId ? { ...chat, ...updates } : chat)));
  };

  const addArtifact = (file) => {
    const artifact = {
      id: Date.now(),
      name: file.name,
      type: file.type,
      size: file.size,
      preview: file.preview,
      url: file.remoteUrl,
      timestamp: new Date(),
      chatId: selectedChatId || currentSessionId, // Associate with current chat
    };

    setArtifacts((prev) => [artifact, ...prev]);
    return artifact;
  };

  const getFriendlyWelcomeMessage = () => {
    const hour = new Date().getHours();

    if (hour < 12) {
      return "Good morning. I'm Dockie, and I'm here to help.\n\nHowever you'd like to start your day, whether it's logistics, shipping, advice, or just something you need to think through, feel free to share and we'll figure it out together.";
    }

    if (hour < 17) {
      return "Good afternoon. I'm Dockie, and I'm here to help.\n\nMidday can get busy, so whatever's on your plate right now, logistics, a decision you're weighing, or anything else, just bring it to me and we'll work through it.";
    }

    if (hour < 21) {
      return "Good evening. I'm Dockie, and I'm here to help.\n\nWhether you're wrapping up the day or planning ahead, I'm happy to assist with whatever you need, big or small.";
    }

    return "Hey, good to have you here. I'm Dockie, and I'm here to help.\n\nNo matter the hour, if something's on your mind or you need to get something done, I'm ready whenever you are.";
  };

  const handleUserMessage = (message) => {
    const { askedForName, name } = userPreferences;

    // Check if user is providing their name
    if (!askedForName && !name) {
      // This might be their name, extract it
      const namePattern =
        /(?:i'm|my name is|call me|i am|name's|it's)\s+([a-zA-Z]+)|^([a-zA-Z]+)$|hello,?\s*i'm\s+([a-zA-Z]+)|hi,?\s*i'm\s+([a-zA-Z]+)/i;
      const match = message.match(namePattern);

      if (match) {
        const extractedName = match[1] || match[2] || match[3] || match[4];
        if (extractedName && extractedName.length > 1) {
          setUserPreferences((prev) => ({
            ...prev,
            name: extractedName,
            askedForName: true,
          }));

          return `Nice to meet you, ${extractedName}! 😊 I'll remember that. Now, how can I help you with your shipping needs today?`;
        }
      } else {
        setUserPreferences((prev) => ({
          ...prev,
          askedForName: true,
        }));
      }
    }

    return null; // Let normal processing continue
  };

  const handleUiAction = async ({ componentId, actionId, label, value = null }) => {
    if (!componentId || !actionId) return;

    if (voiceSession.isActive) {
      toast.error("A voice session is active on this chat. End it to continue by text.");
      return;
    }

    if (isTrackingMapUiAction(actionId)) {
      navigate(buildTrackingPagePath(value));
      return;
    }

    const displayLabel = label ?? actionId;
    const userMessage = {
      id: Date.now(),
      role: "user",
      text: displayLabel,
      timestamp: new Date(),
    };

    aguiStreamAbortRef.current?.abort();
    aguiStreamAbortRef.current = new AbortController();

    const agentMsgId = `agui_${Date.now()}`;
    const agentPlaceholder = {
      id: agentMsgId,
      role: "agent",
      text: "",
      uiComponents: [],
      isAgui: true,
      isStreaming: true,
      streamStatus: "thinking",
      streamTools: [],
      timestamp: new Date(),
    };

    setMessages((prev) => [
      ...prev.map((m) =>
        m.isStreaming
          ? {
              ...m,
              isStreaming: false,
              streamStatus: null,
              streamTool: null,
              streamTools: [],
            }
          : m,
      ),
      userMessage,
      agentPlaceholder,
    ]);
    setLoading(true);

    try {
      const { ipTimezone, ipOffsetMinutes } = useUserStore.getState();
      const userTimezone =
        ipTimezone || localStorage.getItem("ip_timezone") || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const storedOffset = localStorage.getItem("ip_offset_minutes");
      const userOffset =
        ipOffsetMinutes !== null && ipOffsetMinutes !== undefined
          ? ipOffsetMinutes
          : storedOffset !== null
            ? Number(storedOffset)
            : -new Date().getTimezoneOffset();

      const urlChatId = window.location.pathname.match(/\/chats\/([^/]+)/)?.[1];
      const threadId =
        selectedChatId ||
        currentSessionId ||
        (urlChatId && urlChatId !== "new" && !urlChatId.startsWith("temp_") ? urlChatId : null) ||
        createAguiIds().threadId;

      const patchAgent = (patch) => {
        setMessages((prev) => prev.map((m) => (m.id === agentMsgId ? { ...m, ...patch } : m)));
      };

      await runAguiStreamTurn({
        threadId,
        isNewSession: false,
        userId: user?.id || "anonymous",
        userContent: null,
        forwardedProps: {
          action: {
            component_id: componentId,
            action_id: actionId,
            value,
            label: displayLabel,
          },
        },
        previousTurnUi: getPreviousAgentUi(messages),
        timeZone: userTimezone,
        offsetMinutes: userOffset,
        onPatch: patchAgent,
        user,
        signal: aguiStreamAbortRef.current.signal,
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        const errText = String(err?.message || "");
        if (errText.includes("VOICE_SESSION_ACTIVE") || errText.includes("voice session is active")) {
          toast.error("A voice session is active on this chat. End it to continue by text.");
          finalizeStreamingPlaceholders();
        } else if (errText.includes("THREAD_TURN_ACTIVE")) {
          toast.error("Another turn is already running on this chat. Wait for it to finish.");
          finalizeStreamingPlaceholders();
        } else {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === agentMsgId
                ? {
                    ...m,
                    isStreaming: false,
                    text: "Something went wrong. Please try that action again.",
                  }
                : m,
            ),
          );
        }
      }
    } finally {
      setLoading(false);
      setMessages((prev) => sanitizeEnrichedChatMessages(prev));
    }
  };

  const handleSendMessage = async (input, file = null, forwardedProps = {}) => {
    if (!input.trim() && !file) return;

    if (voiceSession.isActive) {
      toast.error("A voice session is active on this chat. End it to continue by text.");
      return;
    }

    setVoiceNoteRecorded(false);

    // Handle name extraction and friendly responses
    const friendlyResponse = handleUserMessage(input);

    const userMessage = {
      id: Date.now(),
      role: "user",
      text: input,
      timestamp: new Date(),
      file: file
        ? {
            name: file.name,
            type: file.type,
            size: file.size,
            preview: file.preview,
          }
        : null,
    };

    // Add artifact if file is attached
    if (file) {
      addArtifact(file);
    }

    // Check if this is a new chat or first user message
    let isNewChat = false;
    let isFirstUserMessage = false;
    const welcomeMessage =
      messages.length === 0
        ? {
            role: "agent",
            text: getFriendlyWelcomeMessage(),
            timestamp: new Date(),
          }
        : null;

    if (messages.length === 0) {
      isNewChat = true;
    } else {
      const userMessages = messages.filter((msg) => msg.role === "user");
      if (userMessages.length === 0 && messages.length === 1) {
        isFirstUserMessage = true;
      }
      if (file) {
        setMessages((prev) => [...prev, userMessage]);
      }
    }

    try {
      const { ipTimezone, ipOffsetMinutes } = useUserStore.getState();
      const storedTz = localStorage.getItem("ip_timezone");
      const storedOffset = localStorage.getItem("ip_offset_minutes");
      const userTimezone = ipTimezone || storedTz || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userOffset =
        ipOffsetMinutes !== null && ipOffsetMinutes !== undefined
          ? ipOffsetMinutes
          : storedOffset !== null
            ? Number(storedOffset)
            : -new Date().getTimezoneOffset();

      // Resolve the correct session ID — prefer URL chat ID over stale React state
      const urlChatId = window.location.pathname.match(/\/chats\/([^/]+)/)?.[1];
      const activeChatId =
        selectedChatId ||
        currentSessionId ||
        (urlChatId && urlChatId !== "new" && !urlChatId.startsWith("temp_") ? urlChatId : null) ||
        localStorage.getItem("new_chat_id");

      const requestParams = {
        user_input: input || "Please analyze this file.",
        user_id: user?.id || "anonymous",
      };

      if (activeChatId && !activeChatId.startsWith("temp_")) {
        requestParams.chat_id = activeChatId;
        isNewChat = false;
      } else {
        requestParams.new_session = true;
      }

      let responseData = null;
      let agentMessage;
      const ephemeralTrackingTurn = isTrackLocateIntent(requestParams.user_input);

      if (!file) {
        setLoading(true);
        aguiStreamAbortRef.current?.abort();
        aguiStreamAbortRef.current = new AbortController();

        const agentMsgId = `agui_${Date.now()}`;
        const agentPlaceholder = {
          id: agentMsgId,
          role: "agent",
          text: "",
          uiComponents: [],
          isAgui: false,
          isStreaming: true,
          streamStatus: "thinking",
          streamTools: [],
          timestamp: new Date(),
        };

        const finalizeStreaming = (list) =>
          list.map((m) =>
            m.isStreaming
              ? {
                  ...m,
                  isStreaming: false,
                  streamStatus: null,
                  streamTool: null,
                  streamTools: [],
                }
              : m,
          );

        if (welcomeMessage) {
          setMessages([welcomeMessage, userMessage, agentPlaceholder]);
        } else {
          setMessages((prev) => [...finalizeStreaming(prev), userMessage, agentPlaceholder]);
        }

        const patchAgent = (patch) => {
          setMessages((prev) => prev.map((m) => (m.id === agentMsgId ? { ...m, ...patch } : m)));
        };

        const streamCtx = {
          userId: requestParams.user_id,
          timeZone: userTimezone,
          offsetMinutes: userOffset,
        };

        if (ephemeralTrackingTurn) {
          const trackingThreadId = createEphemeralTrackingThreadId();
          const code = await resolveTrackingCodeForMap(requestParams.user_input, [], streamCtx);

          if (code) {
            try {
              const mapBlocks = await fetchTrackingMapUiBlocks(code, {
                threadId: trackingThreadId,
                ...streamCtx,
              });
              const ui = filterChatTrackingUi(mapBlocks, requestParams.user_input);
              patchAgent({
                uiComponents: ui,
                isAgui: true,
                isStreaming: false,
                streamStatus: null,
                text: ui.length ? "" : "I couldn't load map data for that shipment. Try a specific tracking number.",
              });
            } catch {
              patchAgent({
                isStreaming: false,
                streamStatus: null,
                text: "Unable to load tracking map. Please try again.",
              });
            }
          } else {
            patchAgent({
              isStreaming: false,
              streamStatus: null,
              text: "I couldn't find an active shipment to track. Try a tracking number like US00830407.",
            });
          }
        } else {
          let threadId = activeChatId && !activeChatId.startsWith("temp_") ? activeChatId : createAguiIds().threadId;

          const isNewSession = isNewChat || isFirstUserMessage || requestParams.new_session;

          if (isNewSession || !activeChatId) {
            setCurrentSessionId(threadId);
            setSelectedChatId(threadId);
            navigate(`/chats/${threadId}`, { replace: true });
          }

          const previousTurnUi = getPreviousAgentUi(messages);

          const streamResult = await runAguiStreamTurn({
            threadId,
            isNewSession,
            userId: requestParams.user_id,
            userContent: requestParams.user_input,
            forwardedProps,
            previousTurnUi,
            timeZone: userTimezone,
            offsetMinutes: userOffset,
            onPatch: patchAgent,
            user,
            signal: aguiStreamAbortRef.current.signal,
          });

          threadId = streamResult.threadId ?? threadId;
          setCurrentSessionId(threadId);

          const enrichedUi = await enrichUiWithTrackingMap(streamResult.uiComponents ?? [], requestParams.user_input, {
            threadId,
            ...streamCtx,
          });
          const resolvedUi = resolveAgentUiForDisplay({
            text: streamResult.text,
            uiComponents: enrichedUi,
            userPrompt: requestParams.user_input,
            previousTurnUi,
          });
          patchAgent({
            uiComponents: resolvedUi.uiComponents,
            isAgui: resolvedUi.isAgui,
            isStreaming: false,
            streamStatus: null,
          });
          if (resolvedUi.uiComponents?.length) {
            appendAguiTurnCache(threadId, {
              uiComponents: resolvedUi.uiComponents,
              text: streamResult.text,
            });
          }
          if (streamResult.paymentEvent) {
            responseData = { payment_event: streamResult.paymentEvent };
          }
        }
      } else {
        setLoading(true);
        const toDataUri = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file.file);
          });

        const dataUri = await toDataUri(file);
        const formData = new FormData();
        formData.append("user_input", requestParams.user_input);
        formData.append("data_uri", dataUri);

        if (requestParams.chat_id) {
          formData.append("chat_id", requestParams.chat_id);
        }
        if (requestParams.new_session) {
          formData.append("new_session", "true");
        }

        responseData = await chatService.sendMultimodalMessage(
          formData,
          userTimezone,
          userOffset,
          requestParams.user_id,
          requestParams.chat_id,
          requestParams.new_session,
        );

        const parsed = parseAgentResponseWithUi(responseData);
        const sessionId = responseData?.data?.session_id || responseData?.session_id;

        if (sessionId) {
          setCurrentSessionId(sessionId);
        }

        agentMessage = {
          id: Date.now() + 1,
          role: "agent",
          text: parsed.text,
          uiComponents: parsed.uiComponents,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, agentMessage]);
        if (parsed.payment_event) {
          responseData = { payment_event: parsed.payment_event };
        }
      }

      const sessionId = responseData?.data?.session_id || responseData?.session_id;
      const chatId = responseData?.data?.chat_id || responseData?.chat_id;
      if (sessionId && sessionId !== currentSessionId) {
        setCurrentSessionId(sessionId);
      }

      if (ephemeralTrackingTurn) {
        return responseData?.data || responseData;
      }

      // Handle chat history for new conversations
      if (isNewChat) {
        const chatIdToUse = selectedChatId || currentSessionId || `chat_${Date.now()}`;

        const newChat = {
          id: chatIdToUse,
          sessionId: currentSessionId || chatIdToUse,
          title: "New Chat",
          preview: input,
          lastMessage: new Date(),
          isActive: true,
        };

        // Update chat history immediately - ensure new chat appears at top
        setChatHistory((prev) => {
          const updatedHistory = [newChat, ...prev.map((chat) => ({ ...chat, isActive: false }))];
          return updatedHistory;
        });

        // Set the selected chat ID if not already set
        if (!selectedChatId) {
          setSelectedChatId(chatIdToUse);
        }

        // Update chat title with real chat ID from backend
        const realChatId = responseData?.data?.chat_id || responseData?.chat_id;

        if (realChatId && realChatId !== chatIdToUse) {
          setChatHistory((prev) =>
            prev.map((chat) =>
              chat.id === chatIdToUse
                ? {
                    ...chat,
                    id: realChatId,
                    sessionId: realChatId,
                    // title: generatedTitle,
                  }
                : chat,
            ),
          );
          setSelectedChatId(realChatId);
        }
      } else if (selectedChatId) {
        // Update existing chat with new messages and move to top
        const updatedChat = chatHistory.find((chat) => chat.id === selectedChatId);
        if (updatedChat) {
          const newChatData = {
            ...updatedChat,
            lastMessage: new Date(),
            preview: input,
          };
          setChatHistory((prev) => [newChatData, ...prev.filter((chat) => chat.id !== selectedChatId)]);
        } else {
          updateChatInHistory(selectedChatId, {
            lastMessage: new Date(),
            preview: input,
          });
        }
      }

      // Return the response data for payment processing
      return responseData?.data || responseData;
    } catch (err) {
      console.error("Error sending message:", err);

      const errText = String(err?.message || "");
      if (errText.includes("VOICE_SESSION_ACTIVE") || errText.includes("voice session is active")) {
        toast.error("A voice session is active on this chat. End it to continue by text.");
        finalizeStreamingPlaceholders();
        return null;
      }
      if (errText.includes("THREAD_TURN_ACTIVE")) {
        toast.error("Another turn is already running on this chat. Wait for it to finish.");
        finalizeStreamingPlaceholders();
        return null;
      }

      // Only show error message for actual errors, not authentication issues
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        const errorMessage = {
          id: Date.now(),
          role: "agent",
          text: "Oops! I ran into a little hiccup there. 😅 Could you please try that again! I promise I'll do better!",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorMessage]);

        // Update chat history with error message if in existing chat
        if (selectedChatId) {
          updateChatInHistory(selectedChatId, {
            lastMessage: new Date(),
          });
        }
      }
      return null;
    } finally {
      setLoading(false);
      setMessages((prev) => sanitizeEnrichedChatMessages(prev));
    }
  };

  const handleNewChat = async () => {
    if (newChatLoading) return; // Prevent duplicate calls

    setNewChatLoading(true);

    try {
      const userId = user?.id || "anonymous";
      const response = await chatService.createChat(userId, "New Chat", null);

      if (response?.data) {
        const realChatId = response.data.id;

        const newChat = {
          id: realChatId,
          sessionId: realChatId,
          title: "New Chat",
          preview: "",
          lastMessage: new Date(),
          messages: [],
          isActive: true,
        };

        setChatHistory((prev) => [newChat, ...prev.map((c) => ({ ...c, isActive: false }))]);
        setSelectedChatId(realChatId);
        setCurrentSessionId(realChatId);
        // Leave messages empty so the conversation pane shows the Dockie welcome /
        // suggestions screen instead of a pre-filled bubble or an endless loader.
        setMessages([]);
        setMessagesLoading(false);
        setMessagesLoadError(null);
        messagesCacheRef.current[realChatId] = [];
        navigate(`/chats/${realChatId}`, { replace: true });
      }
    } catch (error) {
      console.error("Failed to create new chat:", error);
    } finally {
      setNewChatLoading(false);
    }
  };

  const handleSelectChat = (chatId, isRedirect = false) => {
    if (chatId === selectedChatId) return;

    localStorage.removeItem("current_new_chat_id");

    const chatToSelect = chatHistory.find((chat) => chat.id === chatId);
    const hasCachedMessages = Object.prototype.hasOwnProperty.call(messagesCacheRef.current, chatId);

    if (!applyCachedMessages(chatId)) {
      setMessages([]);
      setMessagesLoading(true);
    }

    setSelectedChatId(chatId);
    setCurrentSessionId(chatId);
    setChatHistory((prev) => prev.map((chat) => ({ ...chat, isActive: chat.id === chatId })));

    const path = `/chats/${chatId}`;
    if (location.pathname !== path) {
      navigate(path, { replace: true });
    }

    if (hasCachedMessages) {
      if (isRedirect) {
        const cached = messagesCacheRef.current[chatId] ?? [];
        const lastUserMessage = cached.filter((m) => m.role === "user").pop();
        if (lastUserMessage) {
          setTimeout(() => handleSendMessage(lastUserMessage.text), 500);
        }
      }
      return;
    }

    void hydrateSelectedChatMessages(chatId).then((apiMessages) => {
      if (!chatToSelect && apiMessages.length) {
        const tempChat = {
          id: chatId,
          sessionId: chatId,
          title: `Chat ${chatId.substring(0, 8)}`,
          preview: apiMessages[apiMessages.length - 1]?.text ?? "",
          lastMessage: apiMessages[apiMessages.length - 1]?.timestamp ?? new Date(),
          isActive: true,
        };

        setChatHistory((prev) => [tempChat, ...prev.map((chat) => ({ ...chat, isActive: false }))]);
      }

      if (isRedirect) {
        const lastUserMessage = apiMessages.filter((m) => m.role === "user").pop();
        if (lastUserMessage) {
          setTimeout(() => {
            handleSendMessage(lastUserMessage.text);
          }, 500);
        }
      }
    });
  };

  const handleRefreshChats = useCallback((updatedChats) => {
    if (Array.isArray(updatedChats)) {
      setChatHistory((prev) => mergeChatListUpdates(prev, updatedChats));
    }
  }, []);

  const chatsRouteElement = (
    <ChatsRoute
      userName={user || user?.firstName || userPreferences.name}
      hasMoreChats={hasMoreChats}
      onLoadMoreChats={loadMoreChats}
      chatsLoadingMore={chatsLoadingMore}
      onSendMessage={handleSendMessage}
      onUiAction={handleUiAction}
      messages={messages}
      loading={loading}
      messagesLoading={messagesLoading}
      messagesLoadError={messagesLoadError}
      chatHistory={chatHistory}
      selectedChatId={selectedChatId}
      onNewChat={handleNewChat}
      onSelectChat={handleSelectChat}
      chatsLoading={chatsLoading}
      newChatLoading={newChatLoading}
      onRefreshChats={handleRefreshChats}
      voiceMode={voiceNote.isActive || voiceNoteRecorded ? "note" : voiceSession.mode}
      voiceStatus={voiceNote.isActive ? voiceNote.status : voiceSession.status}
      userTranscript={voiceSession.userTranscript}
      assistantTranscript={voiceSession.assistantTranscript}
      voiceNoteRecorded={voiceNoteRecorded}
      voiceNoteDraft={voiceNoteDraft}
      voiceNoteAttachment={voiceNoteAttachment}
      onVoiceNoteDraftConsumed={handleVoiceNoteDraftConsumed}
      onVoiceNoteEdited={handleVoiceNoteEdited}
      voiceActive={voiceSession.isActive || voiceNote.isActive}
      voiceSessionError={voiceSession.sessionError}
      onClearVoiceSessionError={handleClearVoiceSessionError}
      onStartVoiceChat={handleStartVoiceChat}
      onStartVoiceNote={handleStartVoiceNote}
      onStopVoiceChat={handleStopVoiceChat}
      onCancelVoice={handleCancelVoice}
      onConfirmVoiceNote={handleConfirmVoiceNote}
      voiceMediaStream={
        voiceNote.isActive ? voiceNote.mediaStream : voiceSession.mediaStream
      }
    />
  );

  const Tracking = () => {
    const sidebarToggleRef = React.useRef(null);
    const sidebarApiRef = React.useRef(null);
    return (
      <Layout
        title="Tracking"
        userName={user || user?.firstName || userPreferences.name}
        hideMobileHeader
        onSidebarToggleRef={sidebarToggleRef}
        sidebarApiRef={sidebarApiRef}
        initialSidebarCollapsed
      >
        <TrackingPage onMenuClick={() => sidebarToggleRef.current?.()} sidebarApiRef={sidebarApiRef} />
      </Layout>
    );
  };

  const Home = () => {
    return (
      <Layout title="Home" userName={user || user?.firstName || userPreferences.name}>
        <Dashboard userProfile={user} />
      </Layout>
    );
  };

  return (
    <HelmetProvider>
      <QueryProvider>
        <ThemeProvider theme={theme}>
          <TourProvider>
            <AuthChecker>
              <CssBaseline />
              <KeyboardAccessibilityHelper />
              <Tour />
              <Suspense fallback={<PageLoader showSidebar={showSidebarLoader} />}>
                <Routes>
                  <Route path="/" element={<Version4 />} />

                  <Route
                    path="/home"
                    element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/chats/:sessionId" element={<ProtectedRoute>{chatsRouteElement}</ProtectedRoute>} />
                  <Route
                    path="/auth/login"
                    element={
                      <ProtectedRoute requireAuth={false}>
                        <LoginPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/auth/signup"
                    element={
                      <ProtectedRoute requireAuth={false} allowUnverified={true}>
                        <SignUp />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/auth/complete-profile"
                    element={
                      <ProtectedRoute>
                        <CompleteProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/auth/forgot-password"
                    element={
                      <ProtectedRoute requireAuth={false}>
                        <ForgotPassword />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/auth/verify-otp"
                    element={
                      <ProtectedRoute requireAuth={false}>
                        <OTPVerification />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/auth/resetpassword"
                    element={
                      <ProtectedRoute requireAuth={false}>
                        <ResetPassword />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route path="/ports" element={<PortRecommendation />} /> */}
                  <Route
                    path="/shipments"
                    element={
                      <ProtectedRoute>
                        <ShipmentsRoute userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shipments/:id"
                    element={
                      <ProtectedRoute>
                        <ShipmentsRoute userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/documents/*"
                    element={
                      <ProtectedRoute>
                        <DocumentsRoute userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-cars/*"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="cars" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-decisions"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="decisions" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-invoices"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="invoices" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-team"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="team" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-dockie"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="dockie" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-containers"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="containers" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/next-containers"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="next" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/purchase-details"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="purchase" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-offers"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="offers" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/work-with-dockie" element={<WorkWithDockie />} />
                  <Route
                    path="/vendor"
                    element={
                      <ProtectedRoute>
                        <Layout title="Your Dockie account" userName={user || user?.firstName || userPreferences.name}>
                          <VendorHome />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-papers"
                    element={
                      <ProtectedRoute>
                        <CustomerRoute page="papers" userName={user || user?.firstName || userPreferences.name} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute>
                        <Layout title="Analytics" userName={user || user?.firstName || userPreferences.name}>
                          <AnalyticsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tracking"
                    element={
                      <ProtectedRoute>
                        <Tracking />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Layout title="Settings" userName={user || user?.firstName || userPreferences.name}>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/transporter/:id" element={<Transporter />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                </Routes>
              </Suspense>
            </AuthChecker>
          </TourProvider>
        </ThemeProvider>
      </QueryProvider>
    </HelmetProvider>
  );
}
